import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Shield, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

const LAST_UPDATED = 'May 20, 2026';

const SECTIONS = [
  {
    id: 'introduction',
    title: 'Introduction',
    body: [
      'Go Digital Step is a digital marketing agency providing services such as SEO, content creation, Google Maps Marketing, website development, and paid advertising.',
      'This Privacy Policy explains how we collect, use, and safeguard personal information when clients and website visitors interact with our services. By using our website, you consent to the practices described in this policy.',
    ],
  },
  {
    id: 'information-we-collect',
    title: 'Information We Collect',
    body: [
      'We may collect the following types of personal information:',
      {
        bullets: [
          ['Personal identifiers', 'name, email address, phone number, physical address, and other details you provide through forms or service interactions.'],
          ['Transaction information', 'payment details and history relevant to the services you purchase from us.'],
          ['Usage data', 'details about your interaction with our website, such as session duration, pages visited, and referral sources.'],
          ['Communications', 'messages, support requests, consultation details, and preferences you share with our team.'],
        ],
      },
    ],
  },
  {
    id: 'how-we-collect',
    title: 'How We Collect Information',
    body: [
      {
        bullets: [
          ['Direct collection', 'information you provide through registration, contact forms, intake forms, consultation requests, or customer service interactions.'],
          ['Automated collection', 'cookies and similar tracking technologies that help us understand website preferences and interactions.'],
          ['Third-party sources', 'information from reliable partners who assist us in providing or improving our services.'],
        ],
      },
    ],
  },
  {
    id: 'purpose',
    title: 'Purpose of Data Collection',
    body: [
      'We use collected information to:',
      {
        list: [
          'Provide, maintain, and improve our digital marketing services.',
          'Personalize your experience and deliver content or recommendations relevant to your business.',
          'Process transactions and communicate updates about our services.',
          'Respond to inquiries, support requests, and consultation submissions.',
          'Meet legal obligations and protect our rights.',
        ],
      },
    ],
  },
  {
    id: 'security',
    title: 'Data Security Measures',
    body: [
      'We use reasonable, industry-standard measures to protect personal information from unauthorized access, alteration, disclosure, or misuse. These practices include:',
      {
        bullets: [
          ['Data encryption', 'sensitive information is protected to help maintain data integrity.'],
          ['Access controls', 'access to personal information is limited to authorized personnel who need it for business purposes.'],
          ['Security reviews', 'we perform checks to identify and reduce potential security risks.'],
        ],
      },
      'No method of transmission over the Internet or electronic storage is completely secure, but we take reasonable steps to safeguard the information you share with us.',
    ],
  },
  {
    id: 'sharing',
    title: 'Data Sharing Policy',
    body: [
      'We do not sell your personal information. We may share information only in limited circumstances:',
      {
        bullets: [
          ['Service providers', 'trusted third parties such as payment processors, hosting providers, analytics tools, or operational partners that help us deliver services.'],
          ['Legal requirements', 'when disclosure is required by law, legal process, or to protect our rights.'],
        ],
      },
      'SMS opt-in information and phone numbers collected for communication purposes are not shared with third parties for marketing.',
    ],
  },
  {
    id: 'cookies',
    title: 'Cookies & Tracking',
    body: [
      'We use cookies and similar technologies to improve your experience, remember preferences, understand website interactions, and support relevant marketing. You may modify your browser settings to decline cookies, though certain website functions may be affected.',
    ],
  },
  {
    id: 'your-rights',
    title: 'Your Rights',
    body: [
      'You have rights concerning your personal information, including:',
      {
        bullets: [
          ['Access', 'review the information we hold about you.'],
          ['Correction', 'ask us to correct inaccurate or incomplete information.'],
          ['Deletion', 'request deletion of personal data where applicable.'],
          ['Opt out', 'unsubscribe from marketing communications at any time.'],
        ],
      },
      'To exercise these rights, please contact us using the details below.',
    ],
  },
  {
    id: 'google-api-services',
    title: 'Google API Services & Limited Use',
    body: [
      'When you connect a Google account to Go Digital Step, we request only the minimum scopes needed to deliver the features you have enabled. Depending on the features you use, this may include:',
      {
        bullets: [
          ['Google Search Console', 'read-only access to search performance data to power SEO reporting and analysis.'],
          ['Google Analytics', 'read-only access to analytics data to power traffic and engagement reporting.'],
          ['Google Business Profile', 'access to manage your business listing on your behalf.'],
          ['Google Indexing API', 'to submit your URLs to Google for indexing.'],
          ['Gmail (send only)', 'permission to send outreach and notification emails on your behalf; we do not read, store, or access your existing email messages.'],
        ],
      },
      'Our use and transfer of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements. Specifically, we:',
      {
        bullets: [
          ['Limited use', 'only use Google user data to provide and improve the features you have explicitly requested.'],
          ['No unauthorized transfer', 'do not transfer Google user data to third parties except as necessary to provide or improve those features, to comply with applicable law, or as part of a merger, acquisition, or sale of assets with appropriate notice.'],
          ['No advertising', 'do not use Google user data for serving advertisements, and do not sell Google user data.'],
          ['No AI/ML training', 'do not use, transfer, or sell Google user data — including any data aggregated, anonymized, or derived from it — to create, train, or improve any generalized or foundational machine learning or artificial intelligence model.'],
          ['No human access', 'do not allow humans to read Google user data unless we have your consent for specific data, it is necessary for security purposes or to comply with applicable law, or the data has been aggregated and anonymized.'],
        ],
      },
      {
        attestation: {
          text: 'Go Digital Step’s use and transfer of information received from Google APIs to any other app will adhere to the Google API Services User Data Policy, including the Limited Use requirements.',
          href: 'https://developers.google.com/terms/api-services-user-data-policy',
          linkLabel: 'Google API Services User Data Policy',
        },
      },
      'We retain Google user data only for as long as needed to provide the features you use, and we delete it when you disconnect your Google account, request deletion, or close your account. You can revoke our access at any time from your Google Account permissions page (myaccount.google.com/permissions).',
      {
        attestation: {
          text: 'To request deletion of the data we hold about you, see our Data Deletion policy.',
          href: '/data-deletion',
          linkLabel: 'View our Data Deletion policy',
        },
      },
    ],
  },
  {
    id: 'changes',
    title: 'Changes to This Policy',
    body: [
      'We may update this policy to reflect changes in our practices or applicable laws. Updates will be posted on this page, and we encourage you to review this policy periodically.',
    ],
  },
  {
    id: 'contact',
    title: 'Contact Us',
    body: [
      'If you have questions or concerns about this Privacy Policy, please contact us:',
      { contact: { email: 'info@godigitalstep.com', location: 'India' } },
    ],
  },
];

function renderBody(body) {
  return body.map((block, i) => {
    if (typeof block === 'string') {
      return <p key={i} className="text-slate-600 dark:text-slate-400 leading-relaxed mb-3">{block}</p>;
    }
    if (block.list) {
      return (
        <ul key={i} className="space-y-1.5 mb-3 ml-1">
          {block.list.map((item, j) => (
            <li key={j} className="flex items-start gap-2 text-slate-600 dark:text-slate-400">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
              <span className="leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      );
    }
    if (block.bullets) {
      return (
        <ul key={i} className="space-y-2 mb-3">
          {block.bullets.map(([label, desc], j) => (
            <li key={j} className="flex items-start gap-2 text-slate-600 dark:text-slate-400">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
              <span className="leading-relaxed"><strong className="text-slate-800 dark:text-slate-200">{label}:</strong> {desc}</span>
            </li>
          ))}
        </ul>
      );
    }
    if (block.attestation) {
      return (
        <div key={i} className="mt-2 mb-3 p-4 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800">
          <p className="text-slate-700 dark:text-slate-300 leading-relaxed italic">
            “{block.attestation.text}”
          </p>
          {block.attestation.href && (
            <a
              href={block.attestation.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 text-indigo-600 hover:underline font-medium"
            >
              {block.attestation.linkLabel || block.attestation.href}
            </a>
          )}
        </div>
      );
    }
    if (block.contact) {
      return (
        <div key={i} className="mt-2 p-4 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 flex items-start gap-3">
          <Mail className="w-5 h-5 text-indigo-500 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-slate-800 dark:text-slate-200">GDS – Go Digital Step</p>
            <a href={`mailto:${block.contact.email}`} className="text-indigo-600 hover:underline">{block.contact.email}</a>
            <p className="text-slate-500 dark:text-slate-400">{block.contact.location}</p>
          </div>
        </div>
      );
    }
    return null;
  });
}

export default function PrivacyPolicyPage() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) { sectionRefs.current[id] = el; observer.observe(el); }
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    setActiveId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 gds-glass border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl">GDS</span>
          </Link>
          <div className="flex items-center gap-4">
            <Button variant="ghost" asChild><Link to="/login">Sign In</Link></Button>
            <Button className="gds-btn-primary" asChild><Link to="/register">Get Started</Link></Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="pt-16 bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700">
        <div className="max-w-7xl mx-auto px-6 py-16 flex items-center gap-6">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <div>
            <p className="text-indigo-200 text-sm font-medium mb-1">Legal</p>
            <h1 className="font-heading font-bold text-4xl text-white">Privacy Policy</h1>
            <p className="text-indigo-200 text-sm mt-1">Last updated: {LAST_UPDATED}</p>
          </div>
        </div>
      </div>

      {/* Intro banner */}
      <div className="bg-indigo-50 dark:bg-indigo-950/30 border-b border-indigo-100 dark:border-indigo-900">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed max-w-3xl">
            Go Digital Step is committed to protecting your privacy and safeguarding the personal information
            you share when you visit our website, request a consultation, or use our digital marketing services.
          </p>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex gap-12">

          {/* Sticky sidebar TOC */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24">
              <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-4">
                On This Page
              </p>
              <nav className="space-y-1">
                {SECTIONS.map(({ id, title }, idx) => (
                  <button
                    key={id}
                    onClick={() => scrollTo(id)}
                    className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                      activeId === id
                        ? 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 font-medium'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-md text-xs font-bold flex items-center justify-center shrink-0 ${
                      activeId === id ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="truncate">{title}</span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0 space-y-6">
            {SECTIONS.map(({ id, title, body }, idx) => (
              <section
                key={id}
                id={id}
                className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden scroll-mt-28"
              >
                {/* Section header */}
                <div className="flex items-center gap-4 px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                  <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
                    {idx + 1}
                  </span>
                  <h2 className="font-heading font-semibold text-slate-900 dark:text-white">{title}</h2>
                </div>
                {/* Section body */}
                <div className="px-6 py-5 text-sm">
                  {renderBody(body)}
                </div>
              </section>
            ))}
          </main>
        </div>
      </div>

      <footer className="py-8 px-6 border-t border-slate-200 dark:border-slate-800 text-center text-sm text-slate-600 dark:text-slate-400">
        <p>© 2026 GDS - Go Digital Step. All rights reserved. &nbsp;·&nbsp;
          <Link to="/privacy-policy" className="hover:text-indigo-600">Privacy Policy</Link>
          &nbsp;·&nbsp;
          <Link to="/terms-and-conditions" className="hover:text-indigo-600">Terms and Conditions</Link>
        </p>
      </footer>
    </div>
  );
}
